/**
 * ERP-277 — operator AI connection monitoring.
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory metric strip and stock grid;
 * Finance section-card language; canonical V2 feedback and confirmation patterns.
 * Desktop/mobile: responsive V2MetricStrip, wrapping actions and V2DataGrid scroll.
 * Light/dark and states: design-system tokens; loading, denied, error, empty and partial.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2ConfirmDialog,
  V2DataGrid,
  V2EmptyState,
  V2Kpi,
  V2MetaStrip,
  V2MetricStrip,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { ApiError } from '../../lib/apiClient.js';
import { displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import {
  AI_CONFIGURATION_STATUS,
  aiMonitoringErrorText,
  aiMonitoringProbeBlockReason,
  aiMonitoringReasonText,
  aiMonitoringResponseStatus,
  getAiMonitoring,
  isAiMonitoringSnapshotStale,
  probeAiMonitoring,
  safeAiMonitoringHost,
  type AiMonitoringReport,
  type AiMonitoringService,
} from '../../lib/operatorAiMonitoringApi.js';
import { AI_MONITORING_GUIDE } from '../../help/aiMonitoringGuide.js';
import { ExplicitPageHelpProvider, PageHelpButton } from '../../help/PageHelp.js';
import { OperatorPage } from './OperatorShell.js';

const KIND_LABEL: Readonly<Record<AiMonitoringService['kind'], string>> = {
  chat: 'گفت‌وگو',
  call_analysis: 'تحلیل تماس',
  stt: 'تبدیل صوت به متن',
  embedding: 'آماده‌سازی دانش',
};

function count(value: string | null): string {
  if (value === null || !/^\d+$/.test(value)) return 'گزارش نشده';
  return BigInt(value).toLocaleString('fa-IR');
}

function timestamp(value: string | null): string {
  return value && Number.isFinite(Date.parse(value)) ? displayJalaliDateTime(value) : 'گزارش نشده';
}

interface PendingProbe {
  readonly service: AiMonitoringService;
  readonly operationKey: string;
}

export function AiMonitoringPage() {
  const [report, setReport] = useState<AiMonitoringReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pending, setPending] = useState<PendingProbe | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const alive = useRef(false);
  const requestVersion = useRef(0);
  const probeInFlight = useRef(false);

  async function load() {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getAiMonitoring();
      if (alive.current && version === requestVersion.current) {
        setReport(result);
        setNow(Date.now());
      }
    } catch (failure) {
      if (alive.current && version === requestVersion.current) {
        setError(failure);
        if (failure instanceof ApiError && (failure.status === 401 || failure.status === 403))
          setReport(null);
      }
    } finally {
      if (alive.current && version === requestVersion.current) setLoading(false);
    }
  }

  useEffect(() => {
    alive.current = true;
    void load();
    // Age the evidence without sending any background requests or provider probes.
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      alive.current = false;
      requestVersion.current += 1;
      window.clearInterval(timer);
    };
  }, []);

  function beginProbe(service: AiMonitoringService) {
    if (!report || error !== null || probeInFlight.current || pending) return;
    if (aiMonitoringProbeBlockReason(service, report, Date.now())) return;
    setProbeError(null);
    setNotice(null);
    setPending({ service, operationKey: crypto.randomUUID() });
  }

  async function confirmProbe() {
    if (!pending || probeInFlight.current) return;
    if (!probeError && report) {
      const blocked = aiMonitoringProbeBlockReason(pending.service, report, Date.now());
      if (blocked) {
        setPending(null);
        setProbeError(blocked);
        return;
      }
    }
    probeInFlight.current = true;
    setProbing(true);
    setProbeError(null);
    try {
      const result = await probeAiMonitoring(
        { serviceId: pending.service.id, configFingerprint: pending.service.configFingerprint },
        pending.operationKey,
      );
      if (!alive.current) return;
      setReport((current) =>
        current
          ? {
              ...current,
              services: current.services.map((service) =>
                service.id === result.serviceId
                  ? { ...service, lastProbe: result.probe, stale: false }
                  : service,
              ),
            }
          : current,
      );
      setPending(null);
      setNotice(
        result.replayed
          ? 'نتیجهٔ همان درخواست قبلی دریافت شد؛ آزمون تازه‌ای شروع نشد.'
          : 'نتیجهٔ درخواست آزمون دریافت شد؛ وضعیت و جزئیات همان سرویس را بررسی کن.',
      );
      await load();
    } catch (failure) {
      if (!alive.current) return;
      setProbeError(aiMonitoringErrorText(failure));
      // A rejected request needs a fresh snapshot. An uncertain request retains its key
      // in the dialog so an explicit retry cannot silently become another paid probe.
      if (
        failure instanceof ApiError &&
        failure.status >= 400 &&
        failure.status < 500 &&
        failure.status !== 408
      ) {
        setPending(null);
        if (failure.status === 401 || failure.status === 403) {
          setReport(null);
          setError(failure);
        } else {
          await load();
        }
      }
    } finally {
      probeInFlight.current = false;
      if (alive.current) setProbing(false);
    }
  }

  const services = report?.services ?? [];
  const snapshotStale = report ? isAiMonitoringSnapshotStale(report, now) : false;
  const responseStatus = (service: AiMonitoringService) =>
    error !== null
      ? { label: 'گزارش تازه دریافت نشد', tone: 'warn' as const }
      : aiMonitoringResponseStatus(service, report!, now);
  const columns: readonly V2GridColumn<AiMonitoringService>[] = [
    {
      key: 'service',
      header: 'سرویس',
      width: 180,
      wrap: true,
      render: (service) => (
        <div style={{ display: 'grid', gap: 4 }}>
          <strong>{service.labelFa}</strong>
          <span style={{ color: 'var(--sk-fg-muted)' }}>{KIND_LABEL[service.kind]}</span>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'آزمون با تأیید',
      width: 185,
      wrap: true,
      hideable: false,
      render: (service) => {
        const blocked =
          error !== null
            ? 'ابتدا گزارش را با موفقیت تازه‌سازی کن.'
            : aiMonitoringProbeBlockReason(service, report!, now);
        return (
          <div style={{ display: 'grid', gap: 6 }}>
            <V2Button
              variant="secondary"
              size="sm"
              disabled={Boolean(blocked) || loading || probing}
              aria-label={`آزمون واقعی ${service.labelFa}`}
              onClick={() => beginProbe(service)}
            >
              آزمون واقعی
            </V2Button>
            {blocked ? <small style={{ color: 'var(--sk-fg-muted)' }}>{blocked}</small> : null}
          </div>
        );
      },
    },
    {
      key: 'configuration',
      header: 'تنظیمات',
      width: 160,
      wrap: true,
      render: (service) => (
        <div style={{ display: 'grid', gap: 6 }}>
          <V2Badge tone={AI_CONFIGURATION_STATUS[service.configuration].tone}>
            {AI_CONFIGURATION_STATUS[service.configuration].label}
          </V2Badge>
          {service.reasonCode ? <small>{aiMonitoringReasonText(service.reasonCode)}</small> : null}
        </div>
      ),
    },
    {
      key: 'response',
      header: 'آخرین پاسخ بررسی‌شده',
      emphasis: 'key',
      width: 200,
      wrap: true,
      hideable: false,
      render: (service) => {
        const status = responseStatus(service);
        return <V2Badge tone={status.tone}>{status.label}</V2Badge>;
      },
    },
    {
      key: 'model',
      header: 'ارائه‌دهنده و مدل',
      width: 240,
      wrap: true,
      render: (service) => (
        <div style={{ display: 'grid', gap: 4, overflowWrap: 'anywhere' }}>
          <span>
            ارائه‌دهنده: <bdi>{service.providerCode ?? 'تعیین نشده'}</bdi>
          </span>
          <span>
            درخواستی: <bdi>{service.requestedModel ?? 'تعیین نشده'}</bdi>
          </span>
          <span>
            نهاییِ تنظیم‌شده: <bdi>{service.resolvedModel ?? 'گزارش نشده'}</bdi>
          </span>
        </div>
      ),
    },
    {
      key: 'host',
      header: 'میزبان',
      width: 170,
      wrap: true,
      render: (service) => (
        <span className="ltr" style={{ overflowWrap: 'anywhere' }}>
          {safeAiMonitoringHost(service.host)}
        </span>
      ),
    },
    {
      key: 'time',
      header: 'زمان آخرین آزمون (UTC)',
      width: 180,
      wrap: true,
      render: (service) =>
        service.lastProbe ? (
          <div style={{ display: 'grid', gap: 4 }}>
            <span>شروع: {timestamp(service.lastProbe.startedAt)}</span>
            <span>پایان: {timestamp(service.lastProbe.completedAt)}</span>
          </div>
        ) : (
          'آزمونی ثبت نشده'
        ),
    },
    {
      key: 'latency',
      header: 'زمان پاسخ',
      align: 'end',
      width: 120,
      render: (service) => {
        const latency = service.lastProbe?.latencyMs;
        return typeof latency === 'number' && Number.isFinite(latency) && latency >= 0
          ? `${latency.toLocaleString('fa-IR')} میلی‌ثانیه`
          : 'گزارش نشده';
      },
    },
    {
      key: 'usage',
      header: 'مصرف گزارش‌شدهٔ آزمون',
      width: 185,
      wrap: true,
      render: (service) =>
        service.lastProbe ? (
          <div style={{ display: 'grid', gap: 4 }}>
            <span>توکن ورودی: {count(service.lastProbe.inputTokens)}</span>
            <span>توکن خروجی: {count(service.lastProbe.outputTokens)}</span>
            {!service.lastProbe.usageReported ? (
              <small>گزارش مصرف کامل نیست؛ مقدار نامعلوم صفر نیست.</small>
            ) : null}
          </div>
        ) : (
          'آزمونی ثبت نشده'
        ),
    },
    {
      key: 'reason',
      header: 'جزئیات پاسخ',
      width: 230,
      wrap: true,
      render: (service) => (
        <div style={{ display: 'grid', gap: 4 }}>
          <span>{aiMonitoringReasonText(service.lastProbe?.reasonCode ?? null)}</span>
          {service.lastProbe?.httpStatus !== null && service.lastProbe?.httpStatus !== undefined ? (
            <span>کد پاسخ: {service.lastProbe.httpStatus.toLocaleString('fa-IR')}</span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <ExplicitPageHelpProvider routeKey="/internal/admin/ai/monitoring" guide={AI_MONITORING_GUIDE}>
      <OperatorPage
        title="پایش اتصال و سرویس‌های هوش مصنوعی"
        actions={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <PageHelpButton />
            <V2Button
              variant="secondary"
              icon="clock"
              disabled={loading || probing}
              onClick={() => void load()}
            >
              {loading ? 'در حال دریافت…' : 'تازه‌سازی گزارش'}
            </V2Button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          <V2Alert tone="info" title="تنظیمات با سلامت اتصال فرق دارد">
            این صفحه و تازه‌سازی فقط گزارش را می‌خوانند. آزمون واقعی هر سرویس با تأیید جداگانه انجام
            می‌شود و ممکن است نزد ارائه‌دهنده هزینهٔ کوچکی داشته باشد؛ از کیف پول سازمان‌ها کم
            نمی‌شود.
          </V2Alert>
          <nav
            aria-label="گزارش‌های مرتبط هوش مصنوعی"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
          >
            <Link to="/internal/admin/ai/usage">مصرف و اجراها</Link>
            <Link to="/internal/admin/ai/quality">کیفیت و خطاها</Link>
            <Link to="/internal/admin/ai/pricing">قیمت‌گذاری</Link>
          </nav>
          {error !== null ? (
            <StatePanel
              variant={
                error instanceof ApiError && error.status === 403
                  ? 'permission_denied'
                  : error instanceof ApiError && error.status === 401
                    ? 'unavailable'
                    : 'network_error'
              }
              title={aiMonitoringErrorText(error)}
              description={
                report
                  ? 'اطلاعات قبلی، گزارش تازه محسوب نمی‌شود؛ تا دریافت موفق، آزمون تازه‌ای شروع نکن.'
                  : 'نبود گزارش به معنی سالم‌بودن یا نبودن سرویس‌ها نیست.'
              }
              action={
                error instanceof ApiError && error.status === 401 ? (
                  <Link to="/internal/admin/login">ورود دوباره به کنسول</Link>
                ) : (
                  <V2Button variant="secondary" disabled={loading} onClick={() => void load()}>
                    تلاش دوباره
                  </V2Button>
                )
              }
            />
          ) : null}
          {probeError && !pending ? (
            <V2Alert tone="danger" title="آزمون انجام نشد">
              {probeError}
            </V2Alert>
          ) : null}
          {notice ? (
            <V2Alert tone="info" title="درخواست آزمون">
              {notice}
            </V2Alert>
          ) : null}
          {loading ? <LoadingState label="در حال دریافت گزارش پایش…" /> : null}
          {report ? (
            <>
              <V2Card>
                <V2CardHeader
                  icon="shield"
                  title="خلاصهٔ گزارش"
                  subtitle="نتیجهٔ آخرین مشاهده؛ تضمین سلامت دائمی نیست"
                />
                <V2MetaStrip
                  items={[
                    { label: 'زمان گزارش (UTC)', value: timestamp(report.generatedAt) },
                    {
                      label: 'اعتبار نتیجه',
                      value: `${report.staleAfterSeconds.toLocaleString('fa-IR')} ثانیه`,
                    },
                    {
                      label: 'فاصلهٔ آزمون‌ها',
                      value: `${report.cooldownSeconds.toLocaleString('fa-IR')} ثانیه`,
                    },
                    {
                      label: 'دسترسی',
                      value: report.canManage ? 'مشاهده و درخواست آزمون' : 'فقط مشاهده',
                    },
                  ]}
                />
                <V2CardBody>
                  <V2MetricStrip>
                    <V2Kpi
                      label="سرویس‌های گزارش‌شده"
                      value={services.length.toLocaleString('fa-IR')}
                    />
                    <V2Kpi
                      label="پاسخ سالم و تازه"
                      value={services
                        .filter((service) => responseStatus(service).tone === 'success')
                        .length.toLocaleString('fa-IR')}
                    />
                    <V2Kpi
                      label="تنظیمات نیازمند بررسی"
                      value={services
                        .filter((service) =>
                          ['missing', 'invalid', 'disabled'].includes(service.configuration),
                        )
                        .length.toLocaleString('fa-IR')}
                    />
                  </V2MetricStrip>
                </V2CardBody>
              </V2Card>
              {snapshotStale ? (
                <V2Alert
                  tone="warn"
                  title="گزارش قدیمی است"
                  action={
                    <V2Button
                      variant="secondary"
                      size="sm"
                      disabled={loading || probing}
                      onClick={() => void load()}
                    >
                      تازه‌سازی گزارش
                    </V2Button>
                  }
                >
                  ابتدا گزارش را تازه‌سازی کن؛ نتیجهٔ قدیمی تأیید سلامت فعلی نیست.
                </V2Alert>
              ) : null}
              {services.some(
                (service) =>
                  service.configuration === 'missing' ||
                  service.configuration === 'invalid' ||
                  service.lastProbe?.status === 'failed',
              ) ? (
                <V2Alert tone="warn" title="بخشی از سرویس‌ها نیاز به بررسی دارند">
                  وضعیت هر ردیف مستقل است؛ مشکل یک سرویس را به همهٔ سرویس‌ها تعمیم نده.
                </V2Alert>
              ) : null}
              <V2DataGrid
                title="اتصال و آخرین آزمون سرویس‌ها"
                subtitle="مدل، تنظیمات و نتیجهٔ واقعی جداگانه نمایش داده می‌شوند"
                icon="star"
                columns={columns}
                rows={services}
                rowKey={(service) => service.id}
                rowTone={(service) => responseStatus(service).tone}
                pageSize={0}
                zebra
                emptyState={
                  <V2EmptyState
                    icon="star"
                    title="سرویسی در گزارش نیست"
                    description="این پاسخِ دریافت‌شده فهرستی ندارد؛ نبود ردیف تأیید سلامت اتصال نیست."
                  />
                }
                legendNote="نتیجهٔ محلی، آزمون‌نشده، قدیمی یا نامشخص تأیید اتصال خارجی نیست. مصرف گزارش‌نشده صفر نیست."
              />
            </>
          ) : null}
        </div>
      </OperatorPage>
      <V2ConfirmDialog
        open={pending !== null}
        title={pending ? `آزمون واقعی ${pending.service.labelFa}` : 'آزمون واقعی'}
        message={
          <span>
            فقط همین سرویس با ورودی ثابت آزموده می‌شود. این درخواست ممکن است هزینهٔ کوچکی نزد
            ارائه‌دهنده داشته باشد؛ از کیف پول هیچ سازمانی کم نمی‌شود. تأیید می‌کنی؟
            {probeError ? (
              <>
                <br />
                <strong>{probeError}</strong>
                <br />
                تلاش دوباره، همین درخواست را با همان شناسه پیگیری می‌کند.
              </>
            ) : null}
          </span>
        }
        confirmLabel={probeError ? 'پیگیری همین درخواست' : 'تأیید و اجرای یک آزمون'}
        loading={probing}
        onConfirm={() => void confirmProbe()}
        onCancel={() => {
          if (!probeInFlight.current) {
            setPending(null);
            setProbeError(null);
          }
        }}
      />
    </ExplicitPageHelpProvider>
  );
}
